'use client';

import type React from 'react';
import { forwardRef, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import type { Link } from '@/app_components/common/Link';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { type TrackEventParams, trackEvent } from '@/app_lib/services/Gtm/utils';
import { cn } from '@/app_lib/utils/utils';

interface ActionableIconOwnProps<E extends 'button' | 'a' | typeof Link> {
  as?: E;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => void;
  trackingData?: TrackEventParams;
}

type ActionableIconProps<E extends 'button' | 'a' | typeof Link> = ActionableIconOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof ActionableIconOwnProps<E>>;

/**
 * A utility function that creates a typed React `forwardRef` wrapper with improved type inference for both props and ref.
 * Fixes the common issue where `forwardRef` does not properly infer generic types.
 *
 * @template T The type of the ref instance.
 * @template P The type of the component props (defaults to `object`).
 * @param render - A render function that receives props and a ref, and returns a React element or null.
 * @returns A React component with forwarded ref and proper type inference for both props and ref.
 */
function fixedForwardRef<T, P = object>(
  render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
): (props: P & React.RefAttributes<T>) => React.ReactElement | null {
  // biome-ignore lint/suspicious/noExplicitAny: <Need to come back and fix this>
  return forwardRef(render as any) as any;
}

/**
 * Polymorphic Icon Button component.
 *
 * Use this when wrapping an icon that should be actionable (hover, focus, etc), such as a button or link.
 *
 * Renders default icon styling and padding with either:
 * - A native `<button/>` element by default
 * - A native `<a/>` element when used with `as="a"` (for external navigation)
 * - Our custom Link component (for internal navigation)
 *
 * This component will not accept any props not associated with it's given element type.
 */
export const ActionableIcon = fixedForwardRef(
  <E extends 'button' | 'a' | typeof Link>(
    {
      as,
      children,
      className,
      disabled,
      onClick,
      trackingData,
      'aria-label': ariaLabel,
      ...rest
    }: ActionableIconProps<E>,
    forwardedRef: React.Ref<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const { pageData } = useContext(GtmContext);
    const id = useId();
    const Element = (as ?? 'button') as React.ElementType;
    const localRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
    const [ariaLabelledBy, setAriaLabelledBy] = useState<string | undefined>();

    // Need to combine the forwarded ref (like from radix) with the ref of this component
    // so we can query for the icon child and set aria-labelledby
    const setRefs = useCallback(
      (node: HTMLButtonElement | HTMLAnchorElement | null) => {
        localRef.current = node;

        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          // non-deprecated assignment, only if ref is object-like
          if (node) {
            forwardedRef.current = node;
          }
        }
      },
      [forwardedRef],
    );

    // This component will ALWAYS be used with an icon child which will have an aria-label
    // We need to find that icon and set aria-labelledby on the actionable wrapper
    useEffect(() => {
      if (!localRef.current) {
        return;
      }
      const child = localRef.current.querySelector('.custom-icon');
      setAriaLabelledBy(child?.getAttribute('id') || ariaLabel);
    }, [ariaLabel]);

    return (
      <Element
        aria-labelledby={ariaLabelledBy}
        className={cn(
          'group/button flex min-h-11 min-w-11 shrink-0 items-center justify-center cursor-pointer md:min-h-fit md:min-w-fit',
          'hover:icon-primary-hover active:icon-primary-pressed disabled:cursor-not-allowed disabled:button-tertiary-disabled',
          className,
        )}
        disabled={disabled}
        id={`actionable-icon-${id}`}
        onClick={(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
          // TODO: check with data team to see if we can track failures
          if (trackingData) {
            trackEvent({ ...pageData, ...trackingData.data }, trackingData.eventType);
          }
          onClick?.(e);
        }}
        ref={setRefs}
        {...rest}
      >
        {children}
      </Element>
    );
  },
);
